import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, HeartHandshake, MessageSquare, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { prefetchRoute } from '@/lib/routePrefetch';

/**
 * MobileBottomNav — one-thumb bottom navigation for phone-only.
 *
 * ─ Only visible on screens < md (768px) — desktop continues using the sidebar.
 * ─ 5 slots: Dashboard, Applications, Visits & Surveys, Complaints, More.
 * ─ Active tab highlighted with a top pill; icon lifts up.
 * ─ Deep-link aware: sub-routes like `/admin/applications/:id/edit` keep the
 *   parent "Apps" tab highlighted. Each tab defines its own `matcher` prefixes
 *   so aliases (e.g. Home also matches `/admin` root) light up correctly.
 * ─ Safe-area inset padding so the home indicator doesn't clip labels on
 *   gesture-navigation phones.
 */
const TABS = [
    {
        key: 'dashboard',
        label: 'Home',
        icon: LayoutDashboard,
        path: '/admin/dashboard',
        // `/admin` (bare) and `/admin/dashboard*` both belong to Home.
        matchers: ['/admin/dashboard', '/admin/reports'],
        exactAliases: ['/admin'],
    },
    {
        key: 'applications',
        label: 'Apps',
        icon: FileText,
        path: '/admin/applications',
        matchers: [
            '/admin/applications',
            '/admin/projects',
            '/admin/leads',
            '/admin/quotations',
            '/admin/sale-invoices',
            '/admin/payment-center',
            '/admin/site-surveys',
        ],
    },
    {
        key: 'visits',
        label: 'Visits',
        icon: HeartHandshake,
        path: '/admin/saathi-bookings',
        matchers: [
            '/admin/saathi-bookings',
            '/admin/staff-tracking',
            '/admin/attendance',
            '/admin/my-attendance',
        ],
    },
    {
        key: 'complaints',
        label: 'Cmpl',
        icon: MessageSquare,
        path: '/admin/complaints',
        matchers: ['/admin/complaints'],
    },
    {
        key: 'more',
        label: 'More',
        icon: MoreHorizontal,
        path: '__more__',
    },
];

/**
 * matchPath — decides whether the current URL should light up a given tab.
 *
 * Uses two mechanisms:
 *  1. `exactAliases` — strict equality (used by Home to also claim `/admin`).
 *  2. `matchers`     — startsWith on `<prefix>` OR `<prefix>/anything`, so
 *                      `/admin/applications` and `/admin/applications/123`
 *                      both match, but `/admin/applications-legacy` does NOT.
 */
function matchPath(pathname, tab) {
    if (!tab || tab.path === '__more__') return false;
    const path = pathname || '/';
    if (Array.isArray(tab.exactAliases) && tab.exactAliases.includes(path)) return true;
    const matchers = Array.isArray(tab.matchers) && tab.matchers.length ? tab.matchers : [tab.path];
    for (const m of matchers) {
        if (!m) continue;
        if (path === m) return true;
        if (path.startsWith(m + '/')) return true;
    }
    return false;
}

/**
 * When multiple tabs match (rare, e.g. overlapping prefixes) we pick the
 * longest matcher to break the tie deterministically.
 */
function bestActiveKey(pathname) {
    let best = null;
    let bestLen = -1;
    for (const t of TABS) {
        if (!matchPath(pathname, t)) continue;
        const longest = Math.max(
            ...(t.matchers || [t.path]).map(m => (m || '').length),
            ...(t.exactAliases || []).map(m => (m || '').length),
        );
        if (longest > bestLen) {
            bestLen = longest;
            best = t.key;
        }
    }
    return best;
}

export default function MobileBottomNav({ onOpenMore }) {
    const nav = useNavigate();
    const loc = useLocation();
    const activeKey = bestActiveKey(loc.pathname);
    return (
        <nav
            className='fixed bottom-0 inset-x-0 z-40 md:hidden bg-background/95 backdrop-blur-md border-t shadow-[0_-2px_8px_rgba(0,0,0,0.06)]'
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            data-testid='mobile-bottom-nav'
        >
            <div className='grid grid-cols-5 h-14'>
                {TABS.map(t => {
                    const active = t.key === activeKey;
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            onClick={() => t.path === '__more__' ? onOpenMore?.() : nav(t.path)}
                            onTouchStart={() => t.path !== '__more__' && prefetchRoute(t.path)}
                            onMouseEnter={() => t.path !== '__more__' && prefetchRoute(t.path)}
                            className={cn(
                                'flex flex-col items-center justify-center gap-0.5 relative transition-colors',
                                active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                            )}
                            data-testid={`bnav-${t.key}`}
                            aria-current={active ? 'page' : undefined}
                        >
                            {active && (
                                <span className='absolute top-0 h-0.5 w-8 rounded-b bg-primary' />
                            )}
                            <Icon className={cn('w-5 h-5 transition-transform', active && '-translate-y-0.5')} strokeWidth={active ? 2.5 : 2} />
                            <span className={cn('text-[10px] leading-none', active && 'font-semibold')}>
                                {t.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </nav>
    );
}
